import { DeliverableType } from 'src/shared/constants/DeliverableType';
import type { ProfileQuery } from 'src/shared/graphql/generated';
import { JobSearchFilters, JobTypeFilters } from 'src/shared/graphql/generated';

import type { FiltersParams } from '../components/Filters/types';

type FilterInputOption = {
  name: string;
  value: string;
};

export const ALL_FILTER_INPUT_OPTION: FilterInputOption = {
  name: 'All',
  value: 'all',
};

export const MEASUREMENT_STATUS_FILTERS_INPUTS: FilterInputOption[] = [
  { name: 'Done', value: JobSearchFilters.Complete },
  { name: 'Processing', value: JobSearchFilters.Processing },
  { name: 'Failed', value: JobSearchFilters.Failed },
  { name: 'Photos Needed', value: JobSearchFilters.PhotosNeeded },
  { name: 'Approval Needed', value: JobSearchFilters.NeedsApproval },
];

const JOB_TYPE_FILTERS_INPUTS: FilterInputOption[] = [
  { name: 'Standard', value: JobTypeFilters.Standard },
  { name: 'Connect', value: JobTypeFilters.Connect },
  { name: 'Prospect', value: JobTypeFilters.Prospect },
  { name: 'New Construction', value: JobTypeFilters.Blueprint },
];

const omitInput = (inputList: FilterInputOption[], valueString: string) => {
  const filteredArray = inputList.filter(input => input.value !== valueString);
  return filteredArray;
};

export const allowedMeasurementStatusFilters = (
  isProspectEnabled: boolean,
  isLeadHomeownerCaptureEnabled: boolean,
) => {
  let measureStatFilters = [...MEASUREMENT_STATUS_FILTERS_INPUTS];

  // if user does not have photo capture / prospect, remove needs_photos from measurement status filters
  if (!isProspectEnabled) {
    measureStatFilters = omitInput(
      measureStatFilters,
      JobSearchFilters.PhotosNeeded,
    );
  }

  // if user does not have lead homeowner capture, remove needs_approval from measurement status filters
  if (!isLeadHomeownerCaptureEnabled) {
    measureStatFilters = omitInput(
      measureStatFilters,
      JobSearchFilters.NeedsApproval,
    );
  }

  return measureStatFilters;
};

export const allowedJobTypeFilters = (
  isProspectEnabled: boolean,
  isLeadHomeownerCaptureEnabled: boolean,
  isBlueprintsEnabled: boolean,
) => {
  // start with the set of all job type filters
  let jobTypeFilters = [...JOB_TYPE_FILTERS_INPUTS];

  // if user does not have photo capture / prospect, remove connect from job type filters
  if (!isProspectEnabled) {
    jobTypeFilters = omitInput(jobTypeFilters, JobTypeFilters.Connect);
  }

  // if user does not have lead homeowner capture, remove prospect from job type filters
  if (!isLeadHomeownerCaptureEnabled) {
    jobTypeFilters = omitInput(jobTypeFilters, JobTypeFilters.Prospect);
  }

  // if user does not have blueprints, remove new construction from job type filters
  if (!isBlueprintsEnabled) {
    jobTypeFilters = omitInput(jobTypeFilters, JobTypeFilters.Blueprint);
  }

  // return the jobTypeFilters plus the "all" option input
  return [ALL_FILTER_INPUT_OPTION, ...jobTypeFilters];
};

export type OrgsAvailableDeliverablesType =
  | ProfileQuery['profile']['user']['orgs'][0]['availableDeliverables']
  | undefined;

export const allowedMeasurementTypeFilters = (
  orgsAvailableDeliverables: OrgsAvailableDeliverablesType,
) => {
  const measurementTypeFilterInputs =
    orgsAvailableDeliverables?.map(deliverable => ({
      name: deliverable.name || '',
      value: deliverable.id.toString(),
    })) || [];

  return [...measurementTypeFilterInputs] as FilterInputOption[];
};

export const translateFiltersToParams = (
  measurementStatusFilters: string[],
  jobTypeFilter: string | null,
  measurementTypeFilters: string[],
  shouldShowExamples: boolean,
  shouldShowArchived: boolean,
) => {
  let filtersParamObj = {};
  if (measurementStatusFilters.length > 0) {
    filtersParamObj = {
      ...filtersParamObj,
      filters: measurementStatusFilters,
    };
  }
  if (jobTypeFilter !== ALL_FILTER_INPUT_OPTION.value) {
    filtersParamObj = {
      ...filtersParamObj,
      jobTypes: [jobTypeFilter].filter(el => el),
    };
  }
  if (measurementTypeFilters?.length > 0) {
    filtersParamObj = {
      ...filtersParamObj,
      deliverableIds: measurementTypeFilters,
    };
  }

  return {
    ...filtersParamObj,
    example: shouldShowExamples,
    archived: shouldShowArchived,
  };
};

export const getDeliverableNameById = (id: number) => {
  const indexOf = Object.values(DeliverableType).indexOf(
    id as unknown as DeliverableType,
  );

  return Object.keys(DeliverableType)[indexOf];
};

const toTitleCase = (str: string) =>
  str[0].toUpperCase() + str.slice(1).toLowerCase();

export const jobTypeDisplayName = (jobType: JobTypeFilters) => {
  if (jobType === JobTypeFilters.Blueprint) {
    return 'New Construction';
  }
  return toTitleCase(jobType);
};

export const generateFilterUrl = (filterParams: FiltersParams) => {
  if (!Object.keys(filterParams).length) return '';

  const queryParams = [];

  // Many filters possible
  if (filterParams?.filters?.length)
    filterParams.filters.forEach(filter =>
      queryParams.push(`filters=${filter}`),
    );
  if (filterParams?.deliverableIds?.length)
    filterParams.deliverableIds.forEach(filter =>
      queryParams.push(`deliverableIds=${filter}`),
    );

  // Only one JobType can be selected at once
  if (filterParams?.jobTypes?.length)
    queryParams.push(`jobTypes=${filterParams.jobTypes[0]}`);

  if (filterParams.example) queryParams.push('example=true');
  if (filterParams.archived) queryParams.push('archived=true');

  return queryParams.join('&');
};
